import { useState } from 'react';
import { Package, ChevronDown, Clock, Mail, User, Tag, MessageSquare, DollarSign, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { api } from '../../lib/api';

const STATUS_STYLE = {
  pending_payment: 'bg-yellow-500/20 text-yellow-400',
  pending: 'bg-yellow-500/20 text-yellow-400',
  paid: 'bg-blue-500/20 text-blue-400',
  accepted: 'bg-blue-500/20 text-blue-400',
  delivered: 'bg-purple-500/20 text-purple-400',
  completed: 'bg-green-500/20 text-green-400',
  cancelled: 'bg-red-500/20 text-red-400',
  denied: 'bg-red-500/20 text-red-400',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return iso; }
}

/** Compact key/value row inside the expanded card. */
function Field({ icon: Icon, label, children, testId }) {
  return (
    <div className="flex items-start gap-2 text-[11px]" data-testid={testId}>
      <Icon className="w-3 h-3 text-lime shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[9px] uppercase tracking-wide text-[#666] font-medium">{label}</p>
        <div className="text-[#e4e4e7] break-words">{children}</div>
      </div>
    </div>
  );
}

/** Deliver control that reveals an export-picker inline. */
function DeliverBlock({ order, exports, onDelivered }) {
  const [picking, setPicking] = useState(false);
  const [selectedExportId, setSelectedExportId] = useState('');
  const [delivering, setDelivering] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const selectedExport = exports.find((e) => e.id === selectedExportId);
  const alreadyDelivered = !!selectedExport?.delivered_at;

  const performDelivery = async () => {
    if (!selectedExportId) return;
    setDelivering(true);
    try {
      await api.deliverStudioExport(selectedExportId, order.id);
      toast.success('Dubplate delivered!');
      onDelivered?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delivery failed.');
    } finally {
      setDelivering(false);
      setConfirmOpen(false);
    }
  };

  const submit = () => {
    if (!selectedExportId) { toast.error('Pick a saved session to deliver.'); return; }
    if (alreadyDelivered) { setConfirmOpen(true); return; }
    performDelivery();
  };

  if (!picking) {
    return (
      <Button
        size="sm"
        className="btn-primary h-8 w-full text-[11px]"
        onClick={() => setPicking(true)}
        data-testid={`order-deliver-start-${order.id}`}
      >
        <Send className="w-3 h-3 mr-1.5" />Deliver dubplate
      </Button>
    );
  }

  if (exports.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-[#333] p-2 text-[10px] text-[#888] leading-snug"
           data-testid={`order-deliver-empty-${order.id}`}>
        No saved sessions yet. Record a take, hit Export Stems, then come back to deliver it.
        <button
          type="button"
          onClick={() => setPicking(false)}
          className="ml-1 text-lime hover:underline"
          data-testid={`order-deliver-empty-close-${order.id}`}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border border-[#2a2a2a] bg-[#0a0a0a] p-2 space-y-2"
           data-testid={`order-deliver-picker-${order.id}`}>
        <p className="text-[10px] uppercase tracking-wide text-[#666] font-medium">
          Pick a saved session
        </p>
        <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
          {exports.map((exp) => {
            const active = selectedExportId === exp.id;
            return (
              <button
                type="button"
                key={exp.id}
                onClick={() => setSelectedExportId(exp.id)}
                className={`w-full text-left px-2 py-1.5 rounded border transition-colors ${
                  active
                    ? 'border-lime bg-lime/10 text-white'
                    : 'border-[#222] bg-[#111] text-[#ccc] hover:border-lime/40'
                }`}
                data-testid={`order-deliver-option-${order.id}-${exp.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-medium truncate">{exp.name}</span>
                  {exp.delivered_at && (
                    <span
                      className="inline-flex items-center gap-0.5 shrink-0 text-[9px] text-lime"
                      title={exp.delivered_song_title ? `Already delivered to "${exp.delivered_song_title}"` : 'Already delivered'}
                    >
                      <CheckCircle2 className="w-2.5 h-2.5" />used
                    </span>
                  )}
                </div>
                <p className="text-[9px] text-[#666] mt-0.5">{fmtDate(exp.created_at)}</p>
              </button>
            );
          })}
        </div>
        <div className="flex gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="border-[#333] h-7 flex-1 text-[11px]"
            onClick={() => { setPicking(false); setSelectedExportId(''); }}
            disabled={delivering}
            data-testid={`order-deliver-cancel-${order.id}`}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="btn-primary h-7 flex-1 text-[11px]"
            onClick={submit}
            disabled={!selectedExportId || delivering}
            data-testid={`order-deliver-confirm-${order.id}`}
          >
            {delivering ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" />Send</>}
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(v) => !delivering && setConfirmOpen(v)}>
        <AlertDialogContent
          className="bg-surface border-[#262626] max-w-sm"
          data-testid={`order-deliver-redeliver-confirm-${order.id}`}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Send this session again?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              This session was already delivered to another order
              {selectedExport?.delivered_song_title ? ` ("${selectedExport.delivered_song_title}")` : ''}.
              Send it anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#333] bg-transparent text-white hover:bg-[#1a1a1a]"
              disabled={delivering}
              data-testid={`order-deliver-redeliver-cancel-${order.id}`}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="btn-primary"
              disabled={delivering}
              onClick={(e) => { e.preventDefault(); performDelivery(); }}
              data-testid={`order-deliver-redeliver-confirm-btn-${order.id}`}
            >
              {delivering ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send anyway'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function OrderRow({ order, exports, onDelivered }) {
  const [open, setOpen] = useState(false);
  const statusClass = STATUS_STYLE[order.status] || 'bg-[#333] text-[#aaa]';
  const canDeliver = order.status === 'accepted' && !order.cancel_requested;
  return (
    <div className="rounded-lg bg-[#111] border border-[#222]" data-testid={`studio-order-${order.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-2 hover:bg-[#161616] transition-colors rounded-lg"
        aria-expanded={open}
        data-testid={`studio-order-toggle-${order.id}`}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium truncate">{order.song_title || 'Untitled dub'}</p>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge className={`text-[10px] capitalize ${statusClass}`}>
              {order.status.replace(/_/g, ' ')}
            </Badge>
            <ChevronDown
              className={`w-3.5 h-3.5 text-[#666] transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
        <p className="text-[10px] text-[#666] truncate mt-0.5">
          for {order.user_name || order.user_email}
          {order.message ? ` • "${order.message.slice(0, 60)}${order.message.length > 60 ? '…' : ''}"` : ''}
        </p>
      </button>

      {open && (
        <div
          className="border-t border-[#222] p-3 space-y-2.5 bg-[#0e0e0e] rounded-b-lg"
          data-testid={`studio-order-details-${order.id}`}
        >
          <Field icon={Tag} label="Order" testId={`order-field-id-${order.id}`}>
            <span className="font-mono text-[10px] text-[#A1A1AA]">#{order.id.slice(0, 8)}</span>
          </Field>
          <Field icon={User} label="Buyer" testId={`order-field-buyer-${order.id}`}>
            {order.user_name || '—'}
          </Field>
          {order.user_email && (
            <Field icon={Mail} label="Contact" testId={`order-field-email-${order.id}`}>
              <a
                href={`mailto:${order.user_email}`}
                className="text-lime hover:underline break-all"
                onClick={(e) => e.stopPropagation()}
              >
                {order.user_email}
              </a>
            </Field>
          )}
          <Field icon={MessageSquare} label="Dub brief" testId={`order-field-brief-${order.id}`}>
            {order.message
              ? <p className="whitespace-pre-wrap leading-snug">{order.message}</p>
              : <span className="text-[#666] italic">No message provided.</span>}
          </Field>
          {order.agent_name && (
            <Field icon={User} label="Booked via" testId={`order-field-agent-${order.id}`}>
              {order.agent_name}
            </Field>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            <Field icon={DollarSign} label="You earn" testId={`order-field-earnings-${order.id}`}>
              <span className="text-lime font-semibold">
                ${(order.artist_earnings ?? order.price ?? 0).toFixed(2)}
              </span>
            </Field>
            <Field icon={Clock} label={order.deadline ? 'Deliver by' : 'Ordered'} testId={`order-field-when-${order.id}`}>
              {fmtDate(order.deadline || order.created_at)}
            </Field>
          </div>

          {canDeliver && (
            <div className="pt-1">
              <DeliverBlock order={order} exports={exports} onDelivered={onDelivered} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function OrdersPanel({ orders, exports = [], onDelivered }) {
  return (
    <div className="space-y-3" data-testid="studio-orders-panel">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <Package className="w-4 h-4 text-lime" />My Orders
      </h3>
      {orders.length === 0 ? (
        <p className="text-xs text-[#666] py-4 text-center">No orders yet.</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {orders.map((o) => (
            <OrderRow key={o.id} order={o} exports={exports} onDelivered={onDelivered} />
          ))}
        </div>
      )}
    </div>
  );
}
